import React from 'react';
import { Mission } from '../../../types/mission';
import ChessBoard from '../../chess/ChessBoard';
import styles from './MissionBriefing.module.css';
import { formatSkillLabel } from '../../../utils/missionDisplay';

/**
 * Props for the MissionBriefing component
 */
interface MissionBriefingProps {
  mission: Mission;
  onStartMission: () => void;
}

/**
 * MissionBriefing component
 * 
 * Displays the briefing for a standard mission.
 */
const MissionBriefing: React.FC<MissionBriefingProps> = ({ mission, onStartMission }) => {

  // --- Helper functions for rendering specific elements --- 

  /**
   * Render difficulty display
   */
  const renderDifficulty = () => {
    return (
      <div className={styles.difficultyBadge}>
        Level {mission.difficulty} {mission.type.charAt(0).toUpperCase() + mission.type.slice(1)} Mission
      </div>
    );
  };
  
  /**
   * Render rewards section (XP and skill points)
   */
  const renderRewards = () => (
    <div className={styles.rewardsSection}>
      <h2 className={styles.sectionTitle}>Rewards</h2>
      <div className={styles.rewardsGrid}>
        <div className={styles.rewardItem}>
          <span className={styles.rewardLabel}>Experience:</span>
          <span className={styles.rewardValue}>{mission.rewards.experience} XP</span>
        </div>
        {Object.entries(mission.rewards.skillPoints).map(([skill, points]) => (
          points > 0 && (
            <div key={skill} className={styles.rewardItem}>
              <span className={styles.rewardLabel}>
                {formatSkillLabel(skill)}:
              </span>
              <span className={styles.rewardValue}>+{points} points</span>
            </div>
          )
        ))}
      </div>
    </div>
  );

  // --- Main component render --- 

  return (
    <div className={styles.container} data-testid="mission-briefing">
      <div className={styles.header}>
        {/* @TODO: Add asset: Top-secret or classified stamp image should be added here */}
        <h1 className={styles.title}>{mission.title}</h1>
        {renderDifficulty()} 
      </div>

      <div className={styles.content}>
        <div className={styles.briefingSection}>
          <h2 className={styles.sectionTitle}>Mission Briefing</h2>
          <p className={styles.briefingText}>{mission.briefing}</p>
        </div>

        <div className={styles.objectiveSection}>
          <h2 className={styles.sectionTitle}>Objective</h2>
          <p className={styles.objectiveText}>{mission.objective}</p>
        </div>

        <div className={styles.successSection}>
          <h2 className={styles.sectionTitle}>Success Criteria</h2>
          <p className={styles.successText}>{mission.successCriteria}</p>
        </div>
        
        {mission.timeLimit && (
          <div className={styles.timeLimitSection}>
            <h2 className={styles.sectionTitle}>Time Limit</h2>
            <p className={styles.timeLimitText}>
              {mission.timeLimit} seconds
            </p>
          </div>
        )}

        {renderRewards()}
      </div>

      <div className={styles.acceptSection}>
        <p className={styles.warningText}>
          This mission will test your chess code-breaking skills. Are you ready, agent?
        </p>
        <button 
          className={styles.startButton}
          onClick={onStartMission}
          aria-label="Accept mission and begin"
          data-testid="start-mission-button"
        >
          Accept Mission
        </button>
      </div>
    </div>
  );
};

export default MissionBriefing; 
